// Sprint 1 — Onboarding post-registro
import { motion } from "motion/react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import { useOnboardingWizard } from "@/context/onboarding-wizard-context";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function addMinutesToClock(hhmm: string, add: number): string {
  const [h, m] = hhmm.split(":").map(Number);
  const t = h * 60 + m + add;
  const nh = Math.floor(t / 60) % 24;
  const nm = t % 60;
  return `${pad2(nh)}:${pad2(nm)}`;
}

export function OnboardingStepDuration() {
  const { state, dispatch } = useOnboardingWizard();
  const { slotDuration, interval } = state;
  const endExample = addMinutesToClock("14:00", slotDuration);
  const nextExample = addMinutesToClock("14:00", slotDuration + interval);
  const previewKey = `${slotDuration}-${interval}`;

  return (
    <div>
      <h1 className={css({ fontSize: "2xl", fontWeight: "bold", color: "#0f172a", mb: "2" })}>
        ¡Genial! Ya tenemos la información básica.
      </h1>
      <p className={css({ fontSize: "sm", color: "#64748b", mb: "8", maxW: "42rem", lineHeight: 1.6 })}>
        Ahora necesitamos información sobre los turnos.
      </p>

      <div className={css({ display: "grid", gap: "6", maxW: "28rem", mb: "8" })}>
        <label className={css({ display: "block" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", display: "block", mb: "2" })}>
            ¿Cuánto tiempo dura cada turno? (minutos)
          </span>
          <input
            type="number"
            min={5}
            max={240}
            value={slotDuration}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "slotDuration", value: Number(e.target.value) || 30 })}
            className={cf.input}
          />
        </label>
        <label className={css({ display: "block" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", display: "block", mb: "2" })}>
            ¿Hay tiempo libre entre cada turno? (minutos)
          </span>
          <input
            type="number"
            min={0}
            max={120}
            value={interval}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "interval", value: Number(e.target.value) || 0 })}
            className={cf.input}
          />
          <span className={css({ fontSize: "xs", color: "#64748b", mt: "1.5", display: "block" })}>
            El tiempo muerto entre un turno y el siguiente. No es obligatorio.
          </span>
        </label>
        <label className={css({ display: "block" })}>
          <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#334155", display: "block", mb: "2" })}>
            ¿Cuántas reservas puede haber en el mismo horario?
          </span>
          <input
            type="number"
            min={1}
            max={20}
            value={state.slotsPerHour}
            onChange={(e) => dispatch({ type: "SET_FIELD", field: "slotsPerHour", value: Number(e.target.value) || 1 })}
            className={cf.input}
          />
          <span className={css({ fontSize: "xs", color: "#64748b", mt: "1.5", display: "block" })}>
            Por ejemplo, si son 2, el horario de las 14:00 podrá ser reservado por 2 personas distintas. (Solo vista previa;
            la concurrencia real llegará en una próxima versión.)
          </span>
        </label>
      </div>

      <motion.div
        key={previewKey}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className={css({
          p: "5",
          rounded: "xl",
          bg: "rgba(99, 102, 241, 0.06)",
          borderWidth: "1px",
          borderColor: "rgba(99, 102, 241, 0.2)",
          maxW: "36rem",
          mb: "8",
        })}
      >
        <p className={css({ fontSize: "sm", color: "#334155", lineHeight: 1.6 })}>
          De esta manera, si un turno es reservado para las <strong>14:00</strong>, finalizará a las{" "}
          <strong>{endExample}</strong> y el próximo inicio disponible será las <strong>{nextExample}</strong>.
        </p>
      </motion.div>

      <button
        type="button"
        onClick={() => dispatch({ type: "NEXT_STEP" })}
        className={cx(
          cf.linkPrimaryOnDark,
          css({
            px: "6",
            py: "3",
            border: "none",
            fontSize: "sm",
            cursor: "pointer",
            bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
          }),
        )}
      >
        Continuar
      </button>
    </div>
  );
}
